@if (loading) {
  <div class="row">
    <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>
  </div>
}

<div class="container px-0">
  <div class="row align-items-center mb-3">
    <div class="col-md-6 d-flex align-items-center mb-2 mb-md-0">
      <span class="member-count-badge">
        <mat-icon class="mr-1">group</mat-icon>
        {{ members?.length || 0 }} {{ 'generic.total-members' | translate }}
      </span>
    </div>
    <div class="col-md-6 d-flex justify-content-md-end align-items-center flex-wrap gap-2">
      @if (members?.length > 0) {
        <mat-form-field appearance="outline" class="search-field mr-2" subscriptSizing="dynamic">
          <mat-icon matPrefix class="mr-2 text-muted">search</mat-icon>
          <input matInput type="text" [ngModel]="searchTerm" (ngModelChange)="onSearchChange($event)"
            placeholder="{{ 'generic.search-for' | translate }}...">
          @if (searchTerm) {
            <button mat-icon-button matSuffix (click)="onSearchChange('')" type="button">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
      }
      @if (canInvite) {
        <button mat-stroked-button class="bg-primary-purple text-white border-purple" (click)="toggleAddMember()">
          <mat-icon class="mr-1">{{ addingMember ? 'close' : 'person_add' }}</mat-icon>
          {{ (addingMember ? 'generic.cancel' : 'components.invite-project-members.invite-member') | translate }}
        </button>
      }
    </div>
  </div>

  @if (addingMember) {
    <form #memberForm="ngForm" (ngSubmit)="addMember(memberForm)" class="mb-4">
      <mat-card class="login-card">
        <mat-card-content>
          <div class="row">
            <div class="col-md-4">
              <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                <mat-label>{{ 'generic.name' | translate }}</mat-label>
                <input matInput name="name" [(ngModel)]="newMember.name">
              </mat-form-field>
            </div>
            <div class="col-md-4">
              <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                <mat-label>{{ 'generic.surname' | translate }}</mat-label>
                <input matInput name="last_name" [(ngModel)]="newMember.last_name">
              </mat-form-field>
            </div>
            <div class="col-md-4">
              <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                <mat-label>{{ 'generic.email' | translate }}</mat-label>
                <input matInput type="email" name="email" required email [(ngModel)]="newMember.email">
              </mat-form-field>
            </div>
          </div>
          <div class="row align-items-center">
            <div class="col-md-8">
              <mat-checkbox name="can_invite_others" [(ngModel)]="newMember.can_invite_others">
                {{ 'components.invite-project-members.can-invite-others' | translate }}
              </mat-checkbox>
            </div>
            <div class="col-md-4 text-right">
              <button mat-raised-button color="primary" type="submit" [disabled]="!memberForm.form.valid">
                <mat-icon class="mr-1">send</mat-icon>
                {{ 'components.invite-project-members.send-invitation' | translate }}
              </button>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    </form>
  }

  @if (members?.length > 0) {
    <div class="mat-elevation-z8 table-container-rounded">
      <div class="table-scroll-wrapper">
        <table mat-table [dataSource]="dataSource" matSort class="table-primary">

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="col-title left">
              {{ 'generic.name' | translate }}
            </th>
            <td mat-cell *matCellDef="let member" class="left">
              {{ member.name }} {{ member.last_name }}
            </td>
          </ng-container>

          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="left">
              {{ 'generic.email' | translate }}
            </th>
            <td mat-cell *matCellDef="let member" class="left">
              {{ member.email }}
            </td>
          </ng-container>

          <ng-container matColumnDef="invited_at">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="col-narrow">
              {{ 'components.invite-project-members.invited-at' | translate }}
            </th>
            <td mat-cell *matCellDef="let member" class="col-narrow">
              {{ member.created_at | date:'shortDate' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="can_invite_others">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="col-narrow">
              {{ 'components.invite-project-members.can-invite-others' | translate }}
            </th>
            <td mat-cell *matCellDef="let member" class="col-narrow">
              <mat-icon
                [class.text-success]="member.can_invite_others"
                [class.text-muted]="!member.can_invite_others"
                [matTooltip]="(member.can_invite_others ? 'generic.answers.yes' : 'generic.answers.no') | translate">
                {{ member.can_invite_others ? 'check_circle' : 'remove_circle_outline' }}
              </mat-icon>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="col-actions">
              {{ 'generic.actions' | translate }}
            </th>
            <td mat-cell *matCellDef="let member" class="col-actions">
              @if (isSelf(member)) {
                <span class="text-muted small" [matTooltip]="'components.invite-project-members.cannot-manage-self' | translate">
                  —
                </span>
              } @else {
                <button mat-icon-button color="primary" (click)="resendInvitation(member)"
                  [matTooltip]="'components.invite-project-members.resend-invitation' | translate">
                  <mat-icon>send</mat-icon>
                </button>
                <button mat-icon-button color="warn" (click)="removeMember(member)"
                  [matTooltip]="'generic.buttons.delete' | translate">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
          <tr mat-row *matRowDef="let row; columns: columnsToDisplay;"></tr>
        </table>

        @if (dataSource.filteredData.length === 0) {
          <div class="text-center text-muted py-4">
            {{ 'components.invite-project-members.no-search-results' | translate }}
          </div>
        }
      </div>
      <mat-paginator [pageSizeOptions]="[5, 10, 20]" aria-label="Select page of members"></mat-paginator>
    </div>
  } @else if (!loading) {
    <div class="row">
      <div class="col-12 text-center d-flex flex-column align-items-center py-4">
        <img src="assets/img/no-files.png" alt="" class="no-files-img mb-3">
        <p class="purple-color">{{ 'components.invite-project-members.no-members' | translate }}</p>
      </div>
    </div>
  }
</div>
